export type MachinePicklistAction = 'new' | 'continue' | 'blocked';

export interface MachinePicklistListItem {
  id: string;
  machineId: string;
  generatedAt: string;
  totalItemsToPick: number;
  skuCount: number;
  hasStockShortages: boolean;
  refillSessionId: string | null;
  createdByName: string | null;
}

export interface MachinePicklistListResult {
  items: MachinePicklistListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export type PicklistHistoryState = 'open' | 'in_refill';

export function picklistHistoryState(
  refillSessionId: string | null
): PicklistHistoryState {
  return refillSessionId ? 'in_refill' : 'open';
}

export function picklistHistoryLabel(state: PicklistHistoryState): string {
  return state === 'open' ? 'Open' : 'In refill';
}

export function canCreateMachinePicklist(input: {
  unmanaged: boolean;
  hasActiveRefill: boolean;
  latestIsOpen: boolean;
}): MachinePicklistAction {
  if (input.unmanaged || input.hasActiveRefill) return 'blocked';
  if (input.latestIsOpen) return 'continue';
  return 'new';
}

export function machinePicklistHeaderActionLabel(
  action: MachinePicklistAction
): 'Generate Picklist' | 'Continue picklist' | null {
  if (action === 'new') return 'Generate Picklist';
  if (action === 'continue') return 'Continue picklist';
  return null;
}

function requiredString(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Picklist is missing ${field}`);
  }
  return value;
}

function requiredNumber(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Picklist is missing ${field}`);
  }
  return value;
}

export function parseMachinePicklistListItem(value: unknown): MachinePicklistListItem {
  if (value == null || typeof value !== 'object') {
    throw new Error('Invalid picklist');
  }
  const item = value as Record<string, unknown>;
  const generatedAtRaw = item.generatedAt;
  const generatedAt =
    typeof generatedAtRaw === 'string'
      ? generatedAtRaw
      : generatedAtRaw instanceof Date
        ? generatedAtRaw.toISOString()
        : null;
  if (generatedAt == null) {
    throw new Error('Picklist is missing generatedAt');
  }

  return {
    id: requiredString(item.id, 'id'),
    machineId: requiredString(item.machineId, 'machineId'),
    generatedAt,
    totalItemsToPick: requiredNumber(item.totalItemsToPick, 'totalItemsToPick'),
    skuCount: requiredNumber(item.skuCount, 'skuCount'),
    hasStockShortages: item.hasStockShortages === true,
    refillSessionId: typeof item.refillSessionId === 'string' ? item.refillSessionId : null,
    createdByName: typeof item.createdByName === 'string' && item.createdByName.trim().length > 0
      ? item.createdByName.trim()
      : null,
  };
}

export function formatPicklistCreatedBy(createdByName: string | null): string {
  return createdByName && createdByName.trim().length > 0 ? createdByName.trim() : '—';
}

export function parseMachinePicklistList(data: unknown): MachinePicklistListResult {
  if (data == null || typeof data !== 'object') {
    throw new Error('Invalid picklist list response');
  }
  const raw = data as Record<string, unknown>;
  if (!Array.isArray(raw.items)) {
    throw new Error('Invalid picklist list response');
  }

  return {
    items: raw.items.map(parseMachinePicklistListItem),
    total: requiredNumber(raw.total, 'total'),
    page: requiredNumber(raw.page, 'page'),
    pageSize: requiredNumber(raw.pageSize, 'pageSize'),
  };
}

export function parseGeneratedPicklistId(data: unknown): string {
  if (data == null || typeof data !== 'object') {
    throw new Error('Generate picklist returned an invalid response');
  }
  const id = (data as { id?: unknown }).id;
  if (typeof id !== 'string' || id.length === 0) {
    throw new Error('Generate picklist returned an invalid response');
  }
  return id;
}

export function formatPicklistWhen(generatedAt: string): string {
  const date = new Date(generatedAt);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
